import { useEffect, useRef, useState } from "react";
import { callEdgeFunction } from "../lib/supabaseClient";
import type { VerifyHumanResponse } from "../lib/types";
import { useLanguage } from "../i18n/LanguageContext";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

// Cloudflare Turnstile's script (loaded in index.html) defines this global.
// Declared narrowly here rather than pulling in a full @types package for a
// single external script.
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export interface HumanSession {
  sessionToken: string;
  expiresAtMs: number;
}

/**
 * Manages the "is this visitor a verified human" state for the free map -
 * see README, "Zaščita pred boti" and
 * supabase/migrations/20260101000007_free_click_bot_protection.sql.
 *
 * Renders one Cloudflare Turnstile widget (usually invisible/managed - no
 * puzzle to solve for most visitors) into `containerRef` whenever there is
 * no still-valid session, exchanges a passed challenge for a short-lived
 * session_token via verify-human, and exposes that token for free-click
 * calls. `invalidate()` is called by the caller when free-click itself
 * reports the session expired/was rejected server-side, so the widget
 * re-appears without a page reload.
 */
export function useHumanSession() {
  const { t } = useLanguage();
  const [session, setSession] = useState<HumanSession | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  const isValid = !!session && session.expiresAtMs > Date.now();

  useEffect(() => {
    if (isValid) return; // already verified and not yet expired - nothing to render
    if (!siteKey) {
      setError(t("human.siteKeyMissing"));
      return;
    }

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    function tryRender() {
      if (cancelled) return;
      if (!window.turnstile || !containerRef.current) {
        // The Turnstile <script async defer> tag may not have finished
        // loading yet - poll briefly rather than assuming a fixed delay.
        pollTimer = setTimeout(tryRender, 100);
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: async (token: string) => {
          if (cancelled) return;
          setVerifying(true);
          setError(null);
          try {
            const response = await callEdgeFunction<VerifyHumanResponse>("verify-human", {
              turnstileToken: token,
            });
            if (!cancelled) {
              setSession({ sessionToken: response.sessionToken, expiresAtMs: response.expiresAtMs });
            }
          } catch (e) {
            // Deliberately DO NOT auto-reset the widget here. In Managed/
            // invisible mode, reset() makes Turnstile immediately re-solve
            // and re-fire this callback on its own - if verify-human keeps
            // failing (wrong VITE_SUPABASE_URL, ALLOWED_ORIGIN/CORS
            // mismatch, verify-human not deployed, project paused, ...)
            // that turns into a silent infinite retry loop, which is
            // exactly the "keeps refreshing" symptom this used to cause.
            // Show the error and let retry() (a visible button) trigger the
            // next attempt instead.
            if (!cancelled) {
              const message = e instanceof Error ? e.message : t("human.verificationFailedGeneric");
              const isNetworkError = e instanceof TypeError; // fetch() itself failed - see README troubleshooting
              setError(isNetworkError ? t("human.networkError") : message);
            }
          } finally {
            if (!cancelled) setVerifying(false);
          }
        },
        "error-callback": () => {
          if (!cancelled) setError(t("human.verificationFailedRetry"));
        },
      });
    }
    tryRender();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
    // Re-render only when we transition from "have a valid session" to "not"
    // (e.g. expiry, or an explicit invalidate() call) - not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  /** Called when free-click itself reports the session was rejected/expired. */
  function invalidate() {
    setSession(null);
  }

  /**
   * Manually re-solve after an error (see the "Try again" button in
   * MapPage) - an explicit user action, not an automatic loop. Safe to call
   * even if the widget already auto-recovered on its own.
   */
  function retry() {
    setError(null);
    if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
  }

  return {
    session: isValid ? session : null,
    verifying,
    error,
    containerRef,
    invalidate,
    retry,
  };
}
